import { Loading } from "~/components/ui";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { CompanyApi } from "~/api/company-api";
import useUserState from "~/stores/user";
import type { Company } from "~/types/api";
import { useTranslation } from "react-i18next";
import { SoForm } from "../form/so-form";
import { FormFieldType } from "../form/fields";
import { toast } from "sonner";
import { isBulkFormData, type FormOption } from "~/utils/form-util";
import type { BulkFormData } from "../form/so-bulk-form";

export type CompanySelectorProps = {
  onChange: (companyId: number | null) => void;
};

export function CompanySelector(
  { onChange }: CompanySelectorProps = { onChange: () => {} },
) {
  // Fetch the company options
  const { t } = useTranslation();
  const [options, setOptions] = useState<FormOption[]>([]);
  const companyApi = new CompanyApi();
  const { data: companies, isLoading } = useQuery<Company[]>({
    queryKey: ["companies:all"],
    queryFn: async () => {
      const resp = await companyApi.getCompanies();
      if (!resp.ok || resp.data == undefined)
        throw new Error("Failed to fetch companies " + resp.error);

      setOptions(
        resp.data.map(
          (c) =>
            ({
              value: c.companyId,
              label: c.name,
            }) as FormOption<number>,
        ),
      );

      return resp.data;
    },
    staleTime: 0,
  });

  const queryClient = useQueryClient();
  const userState = useUserState();
  function onSubmit(
    data: { selectedCompanyId: number | null } | BulkFormData<any>,
  ) {
    if (isBulkFormData(data)) return;

    data = data as { selectedCompanyId: number | null };
    if (data.selectedCompanyId !== null) {
      // Try to find the company from the "data"
      const foundCompany =
        companies?.find(
          (d: Company) => d.companyId === data.selectedCompanyId,
        ) ?? undefined;
      if (!foundCompany) {
        console.error("Company not found for ID:", data.selectedCompanyId);
        toast.error(t("company.selector.error"));
        onChange(null);
        return;
      }

      userState.setCompany(foundCompany);
      queryClient.invalidateQueries();
      onChange(foundCompany.companyId ?? null);
      toast.success(t("company.selector.changed", { name: foundCompany.name }));
    }
  }
  // Create the form structure
  const structure = {
    selectedCompanyId: {
      label: "company.selector.title",
      placeholder: "company.selector.placeholder",
      type: FormFieldType.COMBOBOX,
      required: true,
      options: options,
      defaultValue: userState.company?.companyId ?? 1,
    },
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <SoForm structure={structure} onSubmit={onSubmit} />
    </>
  );
}
